import React from 'react';

import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowHorizontalFlow from './components/WorkflowHorizontalFlow';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowStepFlow from './components/WorkflowStepFlow';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  {
    label: 'Part4',
    title: 'Phase2: コード生成',
    path: '/devdocs/workflow/be-step4',
    active: true,
  },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const WorkflowBeStep4: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part4 — api_project_rule Phase2: コード生成"
      seriesName="バックエンド開発編"
      tags={['Backend', 'CodeGen', 'Phase2']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part3: Phase1 DD書の作成', path: '/devdocs/workflow/be-step3' }}
      nextLink={{ label: 'Part5: Phase3 単体テスト', path: '/devdocs/workflow/be-step5' }}
    >
      <WorkflowLearningPoints
        items={[
          'mk_instructions.md（実装指示書）の作成方法と記載内容',
          'DD書からJavaコードを自動生成する手順',
          'DD書・mk_instructions・コードの3者整合性を保つ方法',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'Phase2 の全体フロー' },
          { id: 'section-2', label: 'Step 2-1: mk_instructions.md の作成' },
          { id: 'section-3', label: 'Step 2-2: ユーザー確認' },
          { id: 'section-4', label: 'Step 2-3: コード生成' },
          { id: 'section-5', label: 'Step 2-4〜2-8: 後続ステップ' },
          { id: 'section-6', label: '関連するステアリングファイル' },
          { id: 'section-7', label: '注意すべきこと' },
          { id: 'section-8', label: '成果物まとめ' },
          { id: 'section-9', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* セクション1: Phase2 の全体フロー */}
      <WorkflowSection title="Phase2 の全体フロー" id="section-1" number={1}>
        <WorkflowHorizontalFlow
          steps={[
            {
              icon: <DescriptionIcon sx={{ fontSize: 20 }} />,
              label: '入力',
              sub: 'API仕様書 / DD書 / スキーマ定義',
            },
            {
              icon: <BuildIcon sx={{ fontSize: 20 }} />,
              label: '処理',
              sub: 'mk_instructions + コード生成',
            },
            {
              icon: <CodeIcon sx={{ fontSize: 20 }} />,
              label: '出力',
              sub: 'Controller / UseCase / Mapper...',
            },
          ]}
        />
        <WorkflowStepFlow
          steps={[
            { label: '2-1. mk_instructions.md の作成' },
            { label: '2-2. 【実行停止】ユーザー確認', highlight: true },
            { label: '2-3. コード生成' },
            { label: '2-4. 【実行停止】ユーザー確認', highlight: true },
            { label: '2-5. instructions の最終確認' },
            { label: '2-6. mk_instructions とDD書の最終確認' },
            { label: '2-7. ルールの更新' },
            { label: '2-8. 用語集の更新' },
            { label: '【Phase2完了】実行停止', highlight: true },
          ]}
        />
      </WorkflowSection>

      {/* セクション2: Step 2-1 mk_instructions.md の作成 */}
      <WorkflowSection title="Step 2-1: mk_instructions.md の作成" id="section-2" number={2}>
        <WorkflowText>
          DD書の情報を「実装者（AI）が迷わずコードを書ける」レベルに整理した実装指示書を作成します。
        </WorkflowText>

        <WorkflowSection title="確認する入力ファイル" subNumber="2.1">
          <WorkflowTable
            headers={['入力', '確認ポイント']}
            rows={[
              ['API仕様書', 'Request/Response/処理概要'],
              ['スキーマ定義', 'カラム名・型・制約'],
              ['プロパティ定義', '設定値のキー名'],
              ['DD書（全種）', 'クラス構造・処理フロー・SQL'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="mk_instructions.md に含める情報" subNumber="2.2">
          <WorkflowTable
            headers={['セクション', '内容', '例']}
            rows={[
              [
                '新規作成クラスのFQCN',
                'パッケージ名を含む完全修飾クラス名',
                '...domain.usecase.CreatePartnerUsecase',
              ],
              [
                '修正クラスのFQCN',
                '既存クラスへの追加・変更',
                'MasterTypeRepository にメソッド追加',
              ],
              [
                '新規メソッドの詳細',
                'メソッド名、引数、戻り値、処理内容',
                '#usecase(body) の処理フロー',
              ],
              ['修正メソッドの詳細', '何をどう変更するか具体的に', '「fetchXxx メソッドを追加」'],
              [
                '全体的な実装方針',
                'アーキテクチャ上の判断事項',
                '「MasterType検証はGroupクラス経由」',
              ],
              [
                'リスク・確認事項',
                'レビュー時に確認してほしい点',
                '「既存Mapperとのメソッド名重複注意」',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="コードスニペットの記載方針" subNumber="2.3">
          <WorkflowTable
            headers={['方針', '説明']}
            rows={[
              ['import文は記載しない', '実装時に自動補完する'],
              [
                '定型部分は省略可',
                'package宣言、クラス宣言、アノテーション等。初出パターンは1つだけフル記載',
              ],
              ['ロジックの核心のみ記載', 'ビジネスロジック、SQL文、変換処理、バリデーション'],
              ['DTO/Entityはフィールド一覧テーブル', 'コードブロックは使わずテーブル形式'],
              ['単純なsetter/getter詰め替えは省略', 'DD書のフィールド定義から自明なため'],
              ['Mapper SQLは全文記載', 'SQL文は省略不可'],
              ['UseCase/Serviceの処理フロー', '擬似コード or 実コードで記載'],
            ]}
          />

          <WorkflowTipBox title="なぜコードスニペットの記載方針を定めるのか">
            このルールがないと、AIは import文・package宣言・アノテーション等を含むフルコードを
            mk_instructions.md に出力します。結果として mk_instructions
            が実質的にコードそのものになり、以下の問題が起きます。
            {'\n\n'}
            ・mk_instructions のレビューがコードレビューになってしまう:
            目的はコードレビューではなく「実装方針の合意」。方針レベルで確認したいのに、コードの細部に目が行ってしまう
            {'\n'}
            ・コンテキストを大量消費する: フルコードを書き出すとトークン数が膨らみ、AIの精度が下がる
            {'\n'}
            ・修正指示が面倒になる:
            方針を変えたいだけなのに「何行目のこのコードを直して」という粒度の修正になる
            {'\n\n'}
            mk_instructions
            はあくまで「何をどう実装するかの方針書」であり、コードの完全な事前生成ではありません。ロジックの核心部分だけを記載し、定型部分は実装時にAIが規約に沿って自動生成します。
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowInfoBox type="info">
          複数API指定時はPhase1と同様、代表1つにのみ詳細を記載し、他はリンクのみとします。
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション3: Step 2-2 ユーザー確認 */}
      <WorkflowSection title="Step 2-2: ユーザー確認" id="section-3" number={3}>
        <WorkflowText>mk_instructions.md の内容をユーザーが確認します。</WorkflowText>
        <WorkflowTable
          headers={['入力', '結果']}
          rows={[
            ['0:問題なし', 'Step 2-3（コード生成）に進む'],
            ['1:指摘あり', '指摘を反映して 2-1 から再実施'],
          ]}
        />

        <WorkflowSection title="確認ポイント" subNumber="3.1">
          <WorkflowList
            items={[
              '新規作成クラスの一覧に漏れはないか',
              '修正クラスの変更内容は正しいか',
              'SQLが正しく記載されているか',
              '不要なクラスが含まれていないか（今回のAPIで使わないメソッドは対象外）',
              '実装方針に問題はないか',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Step 2-3 コード生成 */}
      <WorkflowSection title="Step 2-3: コード生成" id="section-4" number={4}>
        <WorkflowText>mk_instructions.md とDD書を元に、Javaコードを生成します。</WorkflowText>

        <WorkflowSection title="Rv指摘の確認" subNumber="4.1">
          <WorkflowText>コード生成時、AIは以下を確認します：</WorkflowText>
          <WorkflowList
            items={[
              '設計書Rv指摘.md の過去指摘に該当する箇所がないか',
              '実装Rv指摘.md の過去指摘に該当する箇所がないか',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: Step 2-4〜2-8 後続ステップ */}
      <WorkflowSection title="Step 2-4〜2-8: 後続ステップ" id="section-5" number={5}>
        <WorkflowSection title="Step 2-4: ユーザー確認" subNumber="5.1">
          <WorkflowText>生成されたコードをユーザーが確認します。</WorkflowText>
          <WorkflowTable
            headers={['入力', '結果']}
            rows={[
              ['0:問題なし', 'Step 2-5 に進む'],
              ['1:指摘あり', '指摘を反映して 2-1 から再実施'],
            ]}
          />
          <WorkflowText>確認ポイント：</WorkflowText>
          <WorkflowList
            items={[
              'コーディング規約に従っているか',
              'DD書の処理フローがコードに正しく反映されているか',
              'SQL文がMapper DD書と一致しているか',
              'バリデーションアノテーションが正しいか',
              'import文に不要なものがないか',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Step 2-5: instructions の最終確認" subNumber="5.2">
          <WorkflowText>
            Phase1の instructions.md の指示内容とDD書の内容に乖離がないか最終確認します。
          </WorkflowText>
          <WorkflowList
            items={['乖離があれば instructions.md をDD書に合わせて修正', '実行漏れがあれば実施']}
          />
        </WorkflowSection>

        <WorkflowSection title="Step 2-6: mk_instructions とDD書の最終確認" subNumber="5.3">
          <WorkflowText>コードへの直接指摘修正により生じた不整合を解消します。</WorkflowText>
          <WorkflowTable
            headers={['チェック対象', '対応']}
            rows={[
              ['mk_instructions ≠ コード', 'mk_instructions をコードに合わせて修正'],
              ['DD書 ≠ コード', 'DD書をコードに合わせて修正'],
              ['実行漏れの指示', '実施する'],
            ]}
          />
          <WorkflowInfoBox type="important">
            Phase2完了時点で DD書 = コード = mk_instructions
            の3者が完全に整合している状態を目指します。これが後続のテストフェーズやメンテナンス時の品質を担保します。
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="Step 2-7: ルールの更新" subNumber="5.4">
          <WorkflowText>
            設計書Rv指摘.md / 実装Rv指摘.md の中で ,rule
            付きの指摘をステアリングルールに追加します。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="Step 2-8: 用語集の更新" subNumber="5.5">
          <WorkflowText>新しい物理名を使った場合は用語集.md に追記します。</WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: 関連するステアリングファイル */}
      <WorkflowSection title="関連するステアリングファイル" id="section-6" number={6}>
        <WorkflowTable
          headers={['ファイル', 'Phase2での役割']}
          rows={[
            ['api_project_rule.md', 'ワークフロー全体の進行ルール'],
            ['tech.md', 'Javaコーディング規約（Steering自動適用）'],
            ['structure.md', 'パッケージ構成・クラス命名規則（Steering自動適用）'],
            ['note_rule.md', 'Rv指摘・注釈の記法'],
            ['word_rule.md', '用語集との整合ルール'],
          ]}
        />
        <WorkflowText>
          Phase1では create_dd.md や create_schema.md
          など独自のSteeringルールを用意していましたが、Phase2のコード生成では独自に用意するルールファイルはありません。tech.md
          と structure.md が inclusion: always
          で常時適用されるため、コーディング規約とパッケージ構成はAIが自動的に遵守します。
        </WorkflowText>
      </WorkflowSection>

      {/* セクション7: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-7" number={7}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              'DD書の処理と実コードを一致させる',
              'DD書に書いた処理順序やロジックをコードに忠実に反映する',
            ],
            ['既存コードのパターンを踏襲', '同種のクラスが既にある場合はそのパターンに合わせる'],
            ['テスト前にビルドを通す', 'コード生成後はコンパイルエラーがないか確認する'],
            [
              'Steeringルールの遵守確認',
              'AIが規約を無視したコードを生成していないかレビューで確認する',
            ],
            ['3者の整合性', 'DD書・mk_instructions・コードが全て一致した状態でPhase2を完了する'],
          ]}
        />
      </WorkflowSection>

      {/* セクション8: 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-8" number={8}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '配置先', '利用先']}
            rows={[
              [
                'mk_instructions.md',
                '.kiro/specs/10_API/{API_ID}/{ver}/',
                'コード生成の指示書（実装の根拠）',
              ],
              ['Javaソースコード', 'src/main/java/', 'アプリケーション実行'],
              ['Flywayマイグレーション（追加分）', 'src/main/resources/db/migration/', 'DB構築'],
              ['DD書（最終版）', 'docs/30_DD/', 'メンテナンス時の設計参照'],
            ]}
          />
        </WorkflowOutputBox>
      </WorkflowSection>

      {/* セクション9: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-9" number={9}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'mk_instructions.md でロジックの核心だけを記載する',
              effect:
                '定型部分を省略することでレビューが方針確認に集中でき、コンテキスト消費も抑えられる',
            },
            {
              practice: '既存コードのパターンを1つ確認してから生成する',
              effect:
                '同種クラスの実装パターンに合わせることで、コードベース全体の一貫性が保たれる',
            },
            {
              practice: 'コード生成後にコンパイルを通す',
              effect: 'ビルドエラーを早期に検出し、Phase3に入る前に修正できる',
            },
            {
              practice: 'Step 2-6 で3者整合性を確実に取る',
              effect:
                'DD書・mk_instructions・コードが一致した状態でPhase2を完了すれば、メンテナンス時の混乱を防げる',
            },
            {
              practice:
                'コードレビュー時はSteeringルール（tech.md / structure.md）との一致を確認する',
              effect:
                'AIが規約を逸脱するケースは稀だが、ゼロではない。命名規則・アノテーション・パッケージ配置を確認する',
            },
            {
              practice: '指摘は mk_instructions.md に書き込み、コードを直接手修正しない',
              effect: 'AIに修正させることで mk_instructions とコードの整合性が維持される',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'mk_instructions にフルコードを記載する',
              effect:
                'レビューがコードレビューになり、方針確認の目的を果たさない。トークン消費も増大する。ロジック核心・SQL・処理フローのみ記載し、定型部分は省略する',
            },
            {
              practice: 'コード生成後にDD書を修正しない',
              effect:
                'DD書が古いまま残り、次回修正時にAIが古い設計を参照して誤ったコードを生成する。Step 2-6 でDD書をコードに合わせて必ず更新する',
            },
            {
              practice: 'mk_instructions を確認せずコード生成に進む',
              effect:
                '実装方針の認識ズレが全ファイルに波及し、大規模な再生成が必要になる。Step 2-2 で方針を確認・合意してからコード生成に進む',
            },
            {
              practice: 'DD書に記載がないメソッドを生成する',
              effect:
                '設計書とコードが乖離し、設計書が信頼できなくなる。今回のAPIで利用するメソッドのみを生成する。DD書にないものは作らない',
            },
            {
              practice: 'コードを直接手修正してmk_instructionsとの乖離を放置する',
              effect:
                '次回AIがmk_instructionsを読んだ時に古い方針でコードを上書きする。指摘はmk_instructionsまたはDD書経由で反映する',
            },
            {
              practice: 'Rv指摘.md の過去指摘チェックを省略する',
              effect:
                '過去に修正された同種のバグが再度生成される。コード生成時に設計書Rv指摘.md・実装Rv指摘.md を必ず確認する',
            },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep4;
